import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { RiExpandUpDownLine } from 'react-icons/ri';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import Swal from 'sweetalert2';
import { apiClient } from '../../helper/api';
import { formatUtcToLocal } from '../../helper/utils';
import { InboxMessageBody } from '../../helper/inboxMessageHtml';
import { LightBlueBtnColor } from '../../helper/theme';
import { DocumentViewer } from './DocumentViewer';

const authJson = (token: string) =>
  new Headers({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

/** Matches `ViewAttachments` on Request Signature (`RequestSignature.tsx`). */
const MISC_SIG_VIEW_DATE_FMT = 'MM-DD-YYYY';

const miscSignatureAttachmentsDataGridSx = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  border: 'none',
  fontSize: '14px',
  color: '#475569',
  backgroundColor: '#ffffff',
  '& .MuiDataGridPro-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.35,
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#64748b',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    borderTop: '2px solid #e2e8f0',
    minHeight: '52px !important',
    maxHeight: '52px !important',
  },
  '& .MuiDataGrid-columnHeader': {
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-row': {
    transition: 'background-color 0.15s ease',
    '&:nth-of-type(even)': {
      backgroundColor: '#fafbfc',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9 !important',
    },
    '&.Mui-selected': {
      backgroundColor: '#ebf5fa !important',
      '&:hover': {
        backgroundColor: '#dbeef7 !important',
      },
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155',
    '&:focus': { outline: 'none' },
  },
  '.MuiDataGrid-virtualScroller': {
    border: 'none',
  },
  '& .MuiDataGrid-main': {
    border: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    minHeight: 56,
  },
  '& .MuiTablePagination-root': {
    color: '#64748b',
    fontSize: '13px',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    marginBottom: 0,
  },
  '& .MuiTablePagination-select': {
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  '& .MuiIconButton-root': {
    color: '#64748b',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      color: '#475569',
    },
  },
};

function mapFilerNotes(data: unknown) {
  if (!Array.isArray(data)) return [];
  return data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string }) => ({
    filerNote: item.filerNote ?? '',
    sentBy: item.sentBy,
    sentOn: item.sentOn,
  }));
}

type TagDocRow = {
  id: string | number;
  documentName: string;
  documentUrl: string;
  createdDate?: string;
  createdByUsername?: string;
};

type SigDocRow = {
  signatureRequestDocumentId: number;
  documentName: string;
  documentUrl: string;
  createdByUsername?: string;
  createdDate: string;
};

type ReportTagDialogProps = {
  open: boolean;
  reportTagId: number;
  token: string | undefined;
  onClose: () => void;
  onRefresh?: () => void;
  /** When false, hides Send message (e.g. client read-only tag). Default true. */
  showSendMessage?: boolean;
};

export const MiscReportTagMessageDialog: React.FC<ReportTagDialogProps> = ({
  open,
  reportTagId,
  token,
  onClose,
  onRefresh,
  showSendMessage = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [filerNotes, setFilerNotes] = useState<ReturnType<typeof mapFilerNotes>>([]);
  const [documents, setDocuments] = useState<TagDocRow[]>([]);
  const [sendMessageToClient, setSendMessageToClient] = useState('');
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerDocs, setDocViewerDocs] = useState<{ documentUrl: string; documentName: string }[]>([]);

  useEffect(() => {
    if (!open) {
      setFilerNotes([]);
      setDocuments([]);
      setSendMessageToClient('');
      setDocViewerOpen(false);
      setDocViewerDocs([]);
      return;
    }
    if (!token || !reportTagId) return;

    setLoading(true);
    const notesReq = apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
      method: 'GET',
      headers: authJson(token),
    })
      .then((res) => mapFilerNotes(res?.data))
      .catch(() => []);

    const docsReq = apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
      method: 'GET',
      headers: authJson(token),
    })
      .then((response) => {
        const list = response?.data?.documents;
        if (!Array.isArray(list) || list.length === 0) return [];
        return list.map((doc: Record<string, unknown>, index: number) => ({
          id: (doc.reportTagDocumentId as number) ?? index,
          documentName: (doc.documentName as string) || 'Document',
          documentUrl: (doc.documentUrl as string) ?? (doc.blobAddress as string) ?? '',
          createdDate: (doc.dateCreated as string) ?? (doc.createdDate as string),
          createdByUsername: (doc.createdByUsername as string) ?? (doc.sentBy as string) ?? 'N/A',
        })) as TagDocRow[];
      })
      .catch(() => []);

    Promise.all([notesReq, docsReq]).then(([notes, docs]) => {
      setFilerNotes(notes);
      setDocuments(docs);
    }).finally(() => setLoading(false));
  }, [open, reportTagId, token]);

  const handleClose = () => {
    onClose();
  };

  const downloadTagDocument = async (e: React.MouseEvent, doc: TagDocRow) => {
    e.stopPropagation();
    if (!doc.documentUrl) return;
    try {
      const response = await fetch(doc.documentUrl, { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-cache' });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error', text: 'Failed to download document', icon: 'error', confirmButtonText: 'OK' });
    }
  };

  const handleSendMessageToClient = () => {
    const messageToClient = sendMessageToClient?.trim();
    if (!messageToClient || !reportTagId || !token) return;
    setLoading(true);
    apiClient('/ReportTags/sendMessageToClient', {
      method: 'POST',
      headers: authJson(token),
      body: JSON.stringify({ reportTagId, messageToClient }),
    })
      .then((response) => {
        if (response?.status === false) {
          throw new Error((response as { message?: string })?.message || 'Failed to send reply');
        }
        Swal.fire({
          title: 'Message sent',
          text: 'Your message has been successfully sent to the client.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        setSendMessageToClient('');
        return apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
          method: 'GET',
          headers: authJson(token),
        }).then((res) => {
          setFilerNotes(mapFilerNotes(res?.data));
          onRefresh?.();
        });
      })
      .catch((err: Error) => {
        console.error(err);
        Swal.fire({ title: 'Error', text: err?.message || 'Failed to send reply to client', icon: 'error', confirmButtonText: 'OK' });
      })
      .finally(() => setLoading(false));
  };

  const tagColumns: GridColDef<TagDocRow>[] = [
    {
      field: 'documentName',
      headerName: 'File Name',
      flex: 2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{params.value || 'Document'}</Typography>
      ),
    },
    { field: 'createdByUsername', headerName: 'Uploaded By', flex: 1, minWidth: 120 },
    {
      field: 'createdDate',
      headerName: 'Upload Date',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY'),
    },
    {
      field: 'action',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View" arrow>
            <IconButton
              size="small"
              sx={{ color: LightBlueBtnColor }}
              onClick={() => {
                const row = params.row;
                if (row.documentUrl && row.documentName) {
                  setDocViewerDocs([{ documentUrl: row.documentUrl, documentName: row.documentName }]);
                  setDocViewerOpen(true);
                }
              }}
            >
              <RemoveRedEyeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download" arrow>
            <IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={(e) => downloadTagDocument(e, params.row)}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl" PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#e8f5fe', pr: 1 }}>
          <DialogTitle style={{ color: '#00A9E0', fontSize: '25px', flex: 1 }}>Report Tag</DialogTitle>
          <IconButton onClick={handleClose} aria-label="Close" sx={{ color: '#0d4a63' }}>
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
        {loading && <LinearProgress />}
        <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto 360px',
              columnGap: 2.5,
              rowGap: 1.5,
              alignItems: 'stretch',
            }}
          >
            <Typography
              component="h3"
              sx={{ gridColumn: 1, gridRow: 1, color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3, m: 0 }}
            >
              MESSAGE
            </Typography>
            <Typography
              component="h3"
              sx={{ gridColumn: 2, gridRow: 1, color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3, m: 0 }}
            >
              ATTACHMENTS
            </Typography>
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 2,
                visibility: 'hidden',
                pointerEvents: 'none',
                userSelect: 'none',
                overflow: 'hidden',
              }}
              aria-hidden
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '16px', mb: 1.5, lineHeight: 1.3 }}>
                History
              </Typography>
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ gridColumn: 2, gridRow: 2, color: '#000', fontWeight: 'bold', fontSize: '16px', mb: 1.5, lineHeight: 1.3 }}
            >
              History
            </Typography>
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 3,
                height: 360,
                maxHeight: 360,
                overflowY: 'auto',
                border: '1px solid #ddd',
                p: 2,
                borderRadius: 1,
                bgcolor: '#fff',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              {filerNotes.length > 0 ? (
                filerNotes.map((item, index) => (
                  <Box key={index} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {[item.sentBy, item.sentOn ? formatUtcToLocal(item.sentOn, 'MM/DD/YYYY hh:mm A') : '—'].filter(Boolean).join(' • ')}
                    </Typography>
                    <InboxMessageBody text={item.filerNote} />
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#666' }}>No messages available</Typography>
              )}
            </Box>
            <Box
              sx={{
                gridColumn: 2,
                gridRow: 3,
                height: 360,
                maxHeight: 360,
                overflow: 'hidden',
                border: '1px solid #ddd',
                p: 2,
                borderRadius: 1,
                bgcolor: '#fff',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: 326,
                  maxHeight: 326,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {documents.length > 0 ? (
                  <DataGridPro
                    rows={documents}
                    getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                    columns={tagColumns}
                    pagination
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    disableSelectionOnClick
                    sx={{
                      border: 0,
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                      '& .MuiDataGrid-root': { height: '100%', border: 0 },
                      '& .MuiDataGrid-main': { overflow: 'auto' },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f8fafc',
                        borderTop: '2px solid #e2e8f0',
                        borderBottom: '2px solid #e2e8f0',
                        minHeight: '52px !important',
                        maxHeight: '52px !important',
                      },
                    }}
                    components={{
                      NoRowsOverlay: () => <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>,
                    }}
                  />
                ) : (
                  <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                )}
              </Box>
            </Box>
          </Box>
          {showSendMessage && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Send Message</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your message..."
                value={sendMessageToClient}
                onChange={(e) => setSendMessageToClient(e.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={handleClose} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                <Button
                  onClick={handleSendMessageToClient}
                  variant="contained"
                  disabled={!sendMessageToClient?.trim()}
                  sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}
                >
                  Send
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <DocumentViewer open={docViewerOpen} documents={docViewerDocs} onClose={() => setDocViewerOpen(false)} />
    </>
  );
};

type SignatureDialogProps = {
  open: boolean;
  signatureRequestId: number;
  token: string | undefined;
  onClose: () => void;
};

export const MiscSignatureAttachmentsDialog: React.FC<SignatureDialogProps> = ({
  open,
  signatureRequestId,
  token,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [documentList, setDocumentList] = useState<SigDocRow[]>([]);
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerDocs, setDocViewerDocs] = useState<{ documentUrl: string; documentName: string }[]>([]);

  useEffect(() => {
    if (!open) {
      setDocumentList([]);
      setDocViewerOpen(false);
      setDocViewerDocs([]);
      return;
    }
    if (!token || !signatureRequestId) return;

    setLoading(true);
    apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
      method: 'GET',
      headers: authJson(token),
    })
      .then((response) => {
        const docs = response?.data?.documents;
        setDocumentList(Array.isArray(docs) ? docs : []);
      })
      .catch((err) => {
        console.error(err);
        setDocumentList([]);
        Swal.fire({ title: 'Error', text: 'Failed to fetch documents', icon: 'error', confirmButtonText: 'OK' });
      })
      .finally(() => setLoading(false));
  }, [open, signatureRequestId, token]);

  const downloadSingleDocument = async (e: React.MouseEvent, doc: SigDocRow) => {
    e.stopPropagation();
    if (!doc.documentUrl) return;
    try {
      const response = await fetch(doc.documentUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-cache',
      });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.documentName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error', text: 'Failed to download document', icon: 'error', confirmButtonText: 'OK' });
    }
  };

  const sigColumns: GridColDef<SigDocRow>[] = [
    {
      field: 'documentName',
      headerName: 'Document Name',
      flex: 2,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      type: 'string',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: 'createdByUsername',
      headerName: 'Sent By',
      flex: 1.5,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      type: 'string',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => <div>{params.value ?? 'N/A'}</div>,
    },
    {
      field: 'createdDate',
      headerName: 'Date Uploaded',
      flex: 0.8,
      minWidth: 130,
      align: 'left',
      headerAlign: 'left',
      type: 'date',
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => <div>{formatUtcToLocal(params.value, MISC_SIG_VIEW_DATE_FMT)}</div>,
    },
    {
      field: 'action',
      headerName: 'Action',
      hideSortIcons: true,
      type: 'string',
      flex: 0.6,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'left' }}>
          <Tooltip title="View Document" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const row = params.row;
                if (row.documentUrl && row.documentName) {
                  setDocViewerDocs([{ documentUrl: row.documentUrl, documentName: row.documentName }]);
                  setDocViewerOpen(true);
                }
              }}
            >
              <RemoveRedEyeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download document" arrow>
            <IconButton size="small" onClick={(e) => downloadSingleDocument(e, params.row)}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilePresentOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
              View Attachments
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#ffffff' }} aria-label="close">
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#ffffff', padding: '16px !important', margin: 0 }}>
          <Box overflow="scroll">
            <Box sx={{ p: 3 }}>
              <div style={{ height: '59vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
                {loading && <LinearProgress sx={{ backgroundColor: '#132D3D' }} />}
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <DataGridPro
                    rows={documentList}
                    getRowId={(r) => r.signatureRequestDocumentId}
                    columns={sigColumns}
                    getRowHeight={() => 52}
                    sx={miscSignatureAttachmentsDataGridSx}
                  />
                </div>
              </div>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <DocumentViewer open={docViewerOpen} documents={docViewerDocs} onClose={() => setDocViewerOpen(false)} />
    </>
  );
};
